// Three.js scene root — XR8 pipeline module.
// Sets up the Three.js scene via XR8.Threejs, then kicks off whale preload.
// The loading indicator is a DOM element managed here so it can be shown and
// hidden independently of the instruction overlay.

import * as THREE from 'three';

// XR8.Threejs.pipelineModule() requires window.THREE to be set globally.
// Assign it at module evaluation time so it is available before XR8.run().
(window as unknown as Record<string, unknown>).THREE = THREE;

import { loadWhale } from './whaleLoader';
import { addPosterOverlay } from './posterOverlay';
// import { addDebugAnchorCube } from './debugAnchorCube';
// import { addDebugOriginLine, updateDebugOriginLine } from './debugOriginLine';
import { appConfig } from '../config/appConfig';
import { isHighEndDevice } from '../utils/deviceCapabilities';
import { logger } from '../utils/logger';

const LOADING_INDICATOR_ID = 'ar-loading-indicator';

function showLoadingIndicator(): void {
  const slot = document.getElementById('ar-overlay-slot');
  if (!slot || document.getElementById(LOADING_INDICATOR_ID)) return;
  const el = document.createElement('div');
  el.id = LOADING_INDICATOR_ID;
  el.className = 'ar-loading-indicator';
  slot.appendChild(el);
}

function hideLoadingIndicator(): void {
  document.getElementById(LOADING_INDICATOR_ID)?.remove();
}

/**
 * Returns an XR8 pipeline module that initialises the Three.js scene and
 * starts whale preloading as soon as the AR session opens.
 */
export function sceneRootPipelineModule(): XR8PipelineModule {
  let onResize: (() => void) | null = null;

  return {
    name: 'scene-root',
    onStart: () => {
      // XR8.Threejs.xrScene() returns the Three.js scene wired to the camera.
      const { scene, renderer } = (XR8 as unknown as {
        Threejs: { xrScene(): { scene: THREE.Scene; renderer: THREE.WebGLRenderer } };
      }).Threejs.xrScene();

      // Size the renderer to fill the viewport now that XR8.Threejs has
      // created it. Without this the canvas stays at its default 300×150.
      renderer.setSize(window.innerWidth, window.innerHeight);

      // ── Lighting ──────────────────────────────────────────────────────────
      scene.add(new THREE.AmbientLight(0xffffff, 0.6));
      const sun = new THREE.DirectionalLight(0xffffff, 0.8);
      sun.position.set(0, 10, 5);

      // Tone mapping improves colour accuracy at negligible cost — always on.
      if (appConfig.rendering.enableLighting) {
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        logger.log('ACESFilmic tone mapping enabled');
      }

      // ── Shadows (high-end only) ───────────────────────────────────────────
      const shadowsEnabled = appConfig.rendering.enableShadows && isHighEndDevice();
      if (shadowsEnabled) {
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        sun.castShadow = true;
        sun.shadow.mapSize.set(1024, 1024);
        sun.shadow.camera.near = 0.5;
        sun.shadow.camera.far = 50;
        logger.log('Shadow maps enabled (high-end device)');
      } else {
        logger.log('Shadow maps skipped (baseline device or flag off)');
      }

      scene.add(sun);
      addPosterOverlay(scene);
      // addDebugAnchorCube(scene);
      // addDebugOriginLine(scene);

      // ── Occlusion ─────────────────────────────────────────────────────────
      // XR8's self-hosted open-source release does not expose a depth-occlusion
      // API. Check for the API and skip gracefully if absent.
      if (appConfig.rendering.enableOcclusion) {
        const xr8Any = XR8 as unknown as Record<string, unknown>;
        if (xr8Any.Occlusion || xr8Any.DepthOcclusion) {
          logger.log('XR8 occlusion API detected — configuration TBD');
        } else {
          logger.log('Occlusion API not available — skipping');
        }
      }

      // Keep renderer sized to the viewport on orientation change.
      onResize = () => {
        renderer.setSize(window.innerWidth, window.innerHeight);
        logger.log(`Renderer resized to ${window.innerWidth}×${window.innerHeight}`);
      };
      window.addEventListener('resize', onResize);

      // Start whale preload immediately — runs in parallel with scanning.
      showLoadingIndicator();
      loadWhale(scene)
        .then(() => {
          logger.log('Whale ready in scene (hidden)');
          hideLoadingIndicator();
        })
        .catch(() => {
          // whaleLoadFailed() already called inside loadWhale; just clean up UI.
          hideLoadingIndicator();
        });
    },
    // onUpdate: () => {
    //   updateDebugOriginLine();
    // },
    onDestroy: () => {
      if (onResize) {
        window.removeEventListener('resize', onResize);
        onResize = null;
      }
    },
  };
}
